/**
 * @file <bk-cart-drawer>: the cart dialog (sections/cart-drawer.liquid). It extends BkDialog
 * (@theme/dialog), so the focus trap, Esc, scroll lock, backdrop click, returned focus and the
 * theme-editor select hooks come from there.
 *
 * Markup
 *   <bk-cart-drawer id="CartDrawer" data-mode="drawer|page" data-added-message="…" data-editor-open>
 *     drawer: <dialog aria-labelledby="CartDrawerTitle">… <bk-cart-items>
 *               <p role="status" data-cart-status></p> … </bk-cart-items></dialog>
 *     page:   <p role="status" data-cart-status></p> <div class="cart-toast" data-cart-toast hidden>…</div>
 *   </bk-cart-drawer>
 *
 * Behaviour
 * - `open(trigger)` is the public way to show the cart (PLAN contract). With the cart type
 *   "page" there is no drawer, so it shows the short "added" confirmation instead.
 * - After `bk:cart:update` with source "add" the drawer opens (unless the component that added
 *   already opened it with its own trigger) and the addition is announced in the dialog's live
 *   region; in page mode the confirmation appears for a few seconds, paused while hovered or focused.
 * - Theme editor: re-rendering the section while editing it keeps the drawer open.
 * - Every [data-cart-count] bubble follows the cart, even on pages without <bk-cart-items>.
 * The lines, totals and Cart API calls live in assets/cart-items.js.
 */
import { BkDialog } from '@theme/dialog';
import { subscribe } from '@theme/utils';

/** How long the "added" confirmation stays (paused while hovered or focused). */
const TOAST_MS = 4000;

/** Shared with assets/cart-items.js: one count subscription per page, whichever module loads first. */
const COUNT_SYNC = Symbol.for('bk.cartCountSync');

const countHost = /** @type {Record<symbol, unknown>} */ (/** @type {unknown} */ (document));
if (!countHost[COUNT_SYNC]) {
  countHost[COUNT_SYNC] = subscribe('bk:cart:update', (detail) => {
    const count = detail?.cart?.item_count;
    if (typeof count !== 'number') return;
    for (const bubble of document.querySelectorAll('[data-cart-count]')) {
      if (!(bubble instanceof HTMLElement)) continue;
      bubble.textContent = count > 0 ? String(count) : '';
      bubble.hidden = count === 0;
    }
  });
}

export class BkCartDrawer extends BkDialog {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #toastTimer;

  #openedByEditorLoad = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#unsubscribe = subscribe('bk:cart:update', (detail) => this.#handleCartUpdate(detail));

    const toast = this.#toast;
    if (toast) {
      toast.addEventListener('pointerenter', () => clearTimeout(this.#toastTimer), { signal });
      toast.addEventListener('focusin', () => clearTimeout(this.#toastTimer), { signal });
      toast.addEventListener('pointerleave', () => this.#scheduleToastHide(), { signal });
      toast.addEventListener('focusout', () => this.#scheduleToastHide(), { signal });
    }

    document.addEventListener('shopify:section:load', (event) => this.#handleSectionLoad(event), { signal });
    document.addEventListener(
      'shopify:section:deselect',
      () => {
        if (!this.#openedByEditorLoad) return;
        this.#openedByEditorLoad = false;
        this.close();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#toastTimer);
    super.disconnectedCallback();
  }

  /** @returns {boolean} True when the theme uses the cart page instead of the drawer. */
  get #pageMode() {
    return this.dataset.mode === 'page';
  }

  /** @returns {HTMLElement | null} */
  get #toast() {
    const toast = this.querySelector('[data-cart-toast]');
    return toast instanceof HTMLElement ? toast : null;
  }

  /**
   * Show the cart: the drawer, or the short confirmation when the cart type is "page".
   * @param {HTMLElement | null} [trigger] - Control that opened it; receives focus on close
   */
  open(trigger) {
    if (this.#pageMode) {
      this.#showToast();
      return;
    }
    super.open(trigger);
  }

  /** @param {{ source?: string } | undefined} detail */
  #handleCartUpdate(detail) {
    if (detail?.source !== 'add') return;

    if (this.#pageMode) {
      this.#showToast();
      return;
    }

    // The component that added usually opens the drawer itself with its button as the trigger
    // (focus returns there); wait one frame so that call wins, then make sure the drawer is open.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.open();
      this.#announce(this.dataset.addedMessage ?? '');
    });
  }

  /**
   * Announce in this element's live region (inside the modal dialog, so it is not inert).
   * @param {string} message
   */
  #announce(message) {
    const region = this.querySelector('[data-cart-status]');
    if (!(region instanceof HTMLElement) || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  #showToast() {
    const toast = this.#toast;
    if (!toast) return;
    toast.hidden = false;
    this.#announce(this.dataset.addedMessage ?? '');
    this.#scheduleToastHide();
  }

  #scheduleToastHide() {
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      const toast = this.#toast;
      if (!toast) return;
      if (toast.matches(':hover, :focus-within')) {
        this.#scheduleToastHide();
        return;
      }
      toast.hidden = true;
    }, TOAST_MS);
  }

  /**
   * The theme editor re-renders the section after each setting change: reopen the new drawer so
   * the merchant keeps seeing what they edit.
   * @param {Event} event
   */
  #handleSectionLoad(event) {
    const target = event.target;
    if (this.#pageMode || !(target instanceof Node) || !target.contains(this) || this.isOpen) return;
    this.open();
    this.#openedByEditorLoad = true;
  }
}

if (!customElements.get('bk-cart-drawer')) customElements.define('bk-cart-drawer', BkCartDrawer);
